import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  CallMade as CallIcon,
  PersonPin as PersonPinIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import * as yup from 'yup';

// Типы данных
interface Ticket {
  id: string;
  number: number;
  full_number: string;
  queue_name: string;
  service_name: string;
  client_info?: {
    name?: string;
    phone?: string;
  };
  status: 'waiting' | 'called' | 'in_service' | 'completed' | 'cancelled' | 'no_show';
  priority: 'normal' | 'high' | 'urgent';
  created_at: string;
  operator_name?: string;
  window_number?: number;
}

interface TicketFormData {
  queueId: string;
  clientName?: string;
  clientPhone?: string;
  priority: 'normal' | 'high' | 'urgent';
}

// Схема валидации
const ticketValidationSchema = yup.object({
  queueId: yup.string().required('Выберите очередь'),
  clientPhone: yup.string().matches(/^[\+]?[0-9\s\-\(\)]+$/, 'Неверный формат телефона'),
});

export const TicketsPage: React.FC = () => {
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'view' | 'call'>('create');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const queryClient = useQueryClient();

  // Загрузка данных
  const { data: ticketsData, isLoading, error, refetch } = useQuery(['tickets'], async () => {
    const res = await apiClient.get('/api/tickets');
    return res.data;
  }, { refetchInterval: 10000 });

  const { data: queuesData } = useQuery(['queues'], async () => {
    const res = await apiClient.get('/api/queues');
    return res.data;
  });

  // Мутации
  const createTicketMutation = useMutation(
    (ticketData: TicketFormData) => apiClient.post('/api/tickets', ticketData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tickets']);
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Талон успешно создан', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка создания талона', severity: 'error' });
      },
    }
  );

  const callTicketMutation = useMutation(
    (ticketId: string) => apiClient.put(`/api/tickets/${ticketId}/call`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tickets']);
        setSnackbar({ open: true, message: 'Клиент вызван', severity: 'success' });
      },
    }
  );

  const startServiceMutation = useMutation(
    (ticketId: string) => apiClient.put(`/api/tickets/${ticketId}/start`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tickets']);
        setSnackbar({ open: true, message: 'Обслуживание начато', severity: 'success' });
      },
    }
  );

  const completeTicketMutation = useMutation(
    (ticketId: string) => apiClient.put(`/api/tickets/${ticketId}/complete`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tickets']);
        setSnackbar({ open: true, message: 'Обслуживание завершено', severity: 'success' });
      },
    }
  );

  // Форма
  const formik = useFormik<TicketFormData>({
    initialValues: {
      queueId: '',
      clientName: '',
      clientPhone: '',
      priority: 'normal',
    },
    validationSchema: ticketValidationSchema,
    onSubmit: (values) => {
      createTicketMutation.mutate(values);
    },
  });

  // Обработчики событий
  const handleOpenDialog = (type: 'create' | 'view' | 'call', ticket?: Ticket) => {
    setDialogType(type);
    setSelectedTicket(ticket || null);
    if (type === 'create') {
      formik.resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTicket(null);
    formik.resetForm();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting': return 'warning';
      case 'called': return 'info';
      case 'in_service': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'waiting': return 'Ожидает';
      case 'called': return 'Вызван';
      case 'in_service': return 'Обслуживается';
      case 'completed': return 'Завершен';
      case 'cancelled': return 'Отменен';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      default: return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Управление талонами</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Ошибка загрузки талонов</Alert>
      </Box>
    );
  }

  const tickets: Ticket[] = ticketsData?.data?.tickets || [];
  
  // Фильтрация талонов
  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesSearch = !searchQuery || 
      ticket.full_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.client_info?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Статистика
  const waitingTickets = tickets.filter(t => t.status === 'waiting').length;
  const inServiceTickets = tickets.filter(t => t.status === 'in_service').length;
  const completedToday = tickets.filter(t => t.status === 'completed').length;

  return (
    <>
      <Helmet>
        <title>Управление талонами - ELEKTROM</title>
      </Helmet>
      
      <Box>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Управление талонами
        </Typography>
            <Typography variant="body1" color="text.secondary">
              Создание, отслеживание и управление талонами клиентов
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Обновить данные">
              <IconButton onClick={() => refetch()} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Создать талон
            </Button>
          </Box>
        </Box>

        {/* Статистические карточки */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Всего талонов</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{tickets.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>за сегодня</Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>В ожидании</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{waitingTickets}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>клиентов</Typography>
                  </Box>
                  <AccessTimeIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Обслуживается</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{inServiceTickets}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>сейчас</Typography>
                  </Box>
                  <PersonPinIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Завершено</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{completedToday}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>сегодня</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Основная таблица талонов */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
              <TextField
                placeholder="Поиск по номеру или имени..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Статус"
                >
                  <MenuItem value="all">Все</MenuItem>
                  <MenuItem value="waiting">Ожидает</MenuItem>
                  <MenuItem value="called">Вызван</MenuItem>
                  <MenuItem value="in_service">Обслуживается</MenuItem>
                  <MenuItem value="completed">Завершен</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Номер</TableCell>
                    <TableCell>Клиент</TableCell>
                    <TableCell>Очередь</TableCell>
                    <TableCell align="center">Статус</TableCell>
                    <TableCell align="center">Создан</TableCell>
                    <TableCell align="center">Оператор</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {ticket.full_number}
                          </Typography>
                          {ticket.priority !== 'normal' && (
                            <Chip
                              size="small"
                              label={ticket.priority === 'urgent' ? 'Срочный' : 'Высокий'}
                              color={getPriorityColor(ticket.priority) as any}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {ticket.client_info?.name || 'Без имени'}
                          </Typography>
                          {ticket.client_info?.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {ticket.client_info.phone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{ticket.queue_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {ticket.service_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusText(ticket.status)}
                          color={getStatusColor(ticket.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {new Date(ticket.created_at).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {ticket.operator_name ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>
                              {ticket.operator_name.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{ticket.operator_name}</Typography>
                              {ticket.window_number && (
                                <Typography variant="caption" color="text.secondary">
                                  Окно {ticket.window_number}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Просмотр">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('view', ticket)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {ticket.status === 'waiting' && (
                            <Tooltip title="Вызвать">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => callTicketMutation.mutate(ticket.id)}
                              >
                                <CallIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {ticket.status === 'called' && (
                            <Tooltip title="Начать обслуживание">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => startServiceMutation.mutate(ticket.id)}
                              >
                                <PersonPinIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {ticket.status === 'in_service' && (
                            <Tooltip title="Завершить">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => completeTicketMutation.mutate(ticket.id)}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Диалог создания/просмотра талона */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogType === 'create' && 'Создание нового талона'}
            {dialogType === 'view' && 'Информация о талоне'}
            {dialogType === 'call' && 'Вызов клиента'}
          </DialogTitle>
          
          <form onSubmit={formik.handleSubmit}>
            <DialogContent dividers>
              {dialogType === 'create' && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={formik.touched.queueId && Boolean(formik.errors.queueId)}>
                      <InputLabel>Очередь *</InputLabel>
                      <Select
                        name="queueId"
                        value={formik.values.queueId}
                        onChange={formik.handleChange}
                        label="Очередь *"
                      >
                        {queuesData?.data?.queues?.map((queue: any) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name} ({queue.code})
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.queueId && formik.errors.queueId && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {formik.errors.queueId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Имя клиента"
                      name="clientName"
                      value={formik.values.clientName}
                      onChange={formik.handleChange}
                      placeholder="Необязательно"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Телефон клиента"
                      name="clientPhone"
                      value={formik.values.clientPhone}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.clientPhone && Boolean(formik.errors.clientPhone)}
                      helperText={formik.touched.clientPhone && formik.errors.clientPhone}
                      placeholder="+7 (xxx) xxx-xx-xx"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Приоритет</InputLabel>
                      <Select
                        name="priority"
                        value={formik.values.priority}
                        onChange={formik.handleChange}
                        label="Приоритет"
                      >
                        <MenuItem value="normal">Обычный</MenuItem>
                        <MenuItem value="high">Высокий</MenuItem>
                        <MenuItem value="urgent">Срочный</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              
              {dialogType === 'view' && selectedTicket && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Талон {selectedTicket.full_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Клиент:</Typography>
                    <Typography>{selectedTicket.client_info?.name || 'Без имени'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Телефон:</Typography>
                    <Typography>{selectedTicket.client_info?.phone || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Очередь:</Typography>
                    <Typography>{selectedTicket.queue_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Услуга:</Typography>
                    <Typography>{selectedTicket.service_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Статус:</Typography>
                    <Chip
                      label={getStatusText(selectedTicket.status)}
                      color={getStatusColor(selectedTicket.status) as any}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Создан:</Typography>
                    <Typography>{new Date(selectedTicket.created_at).toLocaleString()}</Typography>
                  </Grid>
                  {selectedTicket.operator_name && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Оператор:</Typography>
                        <Typography>{selectedTicket.operator_name}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Окно:</Typography>
                        <Typography>{selectedTicket.window_number || '-'}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}

              {dialogType === 'call' && selectedTicket && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Вызвать клиента с талоном {selectedTicket.full_number}?
                  </Alert>
                  <Typography>
                    <strong>Клиент:</strong> {selectedTicket.client_info?.name || 'Без имени'}
                  </Typography>
                  <Typography>
                    <strong>Очередь:</strong> {selectedTicket.queue_name}
                  </Typography>
                  <Typography>
                    <strong>Услуга:</strong> {selectedTicket.service_name}
          </Typography>
                </Box>
              )}
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                {dialogType === 'view' ? 'Закрыть' : 'Отмена'}
              </Button>
              {dialogType === 'create' && (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createTicketMutation.isLoading}
                >
                  {createTicketMutation.isLoading ? 'Создание...' : 'Создать талон'}
                </Button>
              )}
              {dialogType === 'call' && selectedTicket && (
                <Button
                  variant="contained"
                  color="info"
                  onClick={() => {
                    callTicketMutation.mutate(selectedTicket.id);
                    handleCloseDialog();
                  }}
                  disabled={callTicketMutation.isLoading}
                >
                  {callTicketMutation.isLoading ? 'Вызов...' : 'Вызвать'}
                </Button>
              )}
            </DialogActions>
          </form>
        </Dialog>
        
        {/* Уведомления */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};


